import type {AlertPayload} from '../../../components/copilot/types';
import type {Signal} from '../../../components/signals-screen/types';
import {
  PolarisAccountKey,
  PolarisBrandKey,
  PolarisChatId,
  PolarisCompetitorKey,
  PolarisGraphType,
  PolarisIsoDateTime,
  PolarisMessageId,
  PolarisMessageStatus,
  PolarisUnixMs,
  PolarisUserId
} from './polaris-api.models';
import {ChatLibraryApiResponse} from '../../../components/copilot/chat-library/models/chat-library.models';

export type PolarisModelVersion = 'v1' | 'v2';

export enum PolarisDataDomain {
  OPEN_WEB = 'OPEN_WEB',
  SOCIAL = 'SOCIAL'
}

export interface PolarisCitation {
  title: string;
  url: string;
  snippet: string;
}

export type PolarisOrigin = 'polaris_homepage' | 'alert_view_page' | 'polaris_wizard' | 'widget' | 'signals_screen';
export type PolarisTurnStatus = 'pending' | 'completed' | 'failed';

export interface PolarisWidgetContext {
  origin?: PolarisOrigin;
  name?: string;
  metrics?: string[];
  myBrand?: string;
  myCompetitors?: string[];
  dateRange?: string | {from: string; to: string};
  countries?: string[];
  device?: string;
}

export interface PolarisGraphSeries {
  name: string;
  y: number[];
  unit: string | null;
}

export interface PolarisGraph {
  title: string;
  type: PolarisGraphType;
  url: string | null;
  x: string[];
  series: PolarisGraphSeries[];
  yIsPercentage: boolean;
  xAxis: string;
  yAxis: string;
}

export interface PolarisWizardContext {
  type: string;
  geos: string[];
  startDate: string;
  endDate: string;
  focusAreas: Record<string, { subQuestions: string[] }>;
}

export interface PolarisChatOverview {
  chatId: PolarisChatId;
  accountKey: PolarisAccountKey;
  userId: PolarisUserId;
  title: string;
  parentChatId: PolarisChatId | null;
  modelVersion?: PolarisModelVersion;
  createdAt: PolarisIsoDateTime;
  lastModified?: PolarisIsoDateTime;
  lastMessageTime?: PolarisIsoDateTime;
  isShared: boolean;
}

export interface PolarisMessagePair {
  id: PolarisMessageId;
  chatId: PolarisChatId;
  userPrompt: string;
  response: string;
  status: PolarisMessageStatus;
  userTimestamp: PolarisUnixMs;
  systemTimestamp: PolarisUnixMs | null;
  followUpQuestions: string[];
  insights: string[] | null;
  creativesURLs: string[];
  graphs: PolarisGraph[];
  originalResponse?: string;
  citations?: PolarisCitation[];
  webInsights?: string;
  chatLibrary?: ChatLibraryApiResponse;
  polarisLambdaRequestId: string | null;
  requestId?: string;
  interactionId?: string;
  error?: string;
}

export interface PolarisTurn {
  id: string;
  chatId: PolarisChatId;
  status: PolarisTurnStatus;
  origin?: PolarisOrigin;
  widgetName?: string;
  widgetContext?: PolarisWidgetContext;
  myBrand?: PolarisBrandKey;
  myCompetitors?: PolarisCompetitorKey[];
  wizard?: PolarisWizardContext;
  alert?: AlertPayload;
  signal?: Signal;
  messagePair: PolarisMessagePair;
}

export interface PolarisChatAggregate {
  overview: PolarisChatOverview;
  turns: PolarisTurn[];
  isThinking: boolean;
  isNewlyCreated?: boolean;
}

